import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { ComboboxComponent } from '@appkit4/angular-components/combobox';
import { ComboboxSearchResult } from '@shared/abstraction';
import { ComboboxItemFilter } from '@shared/abstraction/combobox/combobox-item-filter';
import { BaseDropdownComboboxComponent } from '../base-components/base-dropdown-combobox';
import { delay, timer } from 'rxjs';
import { fromMutationObserver } from '@shared/functions/from-mutation-observer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  standalone: false,
  selector: 'gstar-combobox-filter',
  templateUrl: './combobox-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComboboxFilterComponent
  extends BaseDropdownComboboxComponent
  implements OnInit, AfterViewInit
{
  private readonly destoyRef = inject(DestroyRef);

  highlightText: string = '';

  filteredOptions: WritableSignal<Partial<ComboboxItemFilter>[]> = signal([{}]);

  combobox: Signal<ComboboxComponent | undefined> =
    viewChild<ComboboxComponent>('combobox');

  get isValueEmpty(): boolean {
    return (
      Array.isArray(this.value()) &&
      this.getValue<Array<ComboboxItemFilter>>().length === 0
    );
  }

  constructor() {
    super();
    this.optionsSettingResolver = this.resolver;

    this.clearAll.subscribe(() => {
      if (this.selectedItems.length > 0 || !this.isValueEmpty) {
        this.clearValue(this.options());
      }
      if (this.combobox()) {
        this.combobox()!.comboboxList.nativeElement.scrollTo(0, 0);
      }
    });
  }

  ngAfterViewInit(): void {
    fromMutationObserver(this.combobox()!.listboxEl, {
      attributes: true,
    })
      .pipe(takeUntilDestroyed(this.destoyRef), delay(0))
      .subscribe(() => {
        this.combobox()!.listboxEl.style.top = `${this.combobox()!.el.nativeElement.offsetHeight + 8}px`;
      });
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  filterResultHandler<ComboboxItemFilter>({
    searchStr,
  }: ComboboxSearchResult<ComboboxItemFilter>): void {
    this.highlightText = searchStr.trim();

    if (searchStr.trim() && searchStr.trim().length > 0) {
      const result = this.options().filter(item =>
        item.key
          .toLocaleLowerCase()
          .includes(searchStr.trim().toLocaleLowerCase())
      );
      this.filteredOptions.set(result);
    } else {
      this.filteredOptions.set(this.options());
    }
  }

  closeHandler(): void {
    if (this.selectedItems.length === 0 && this.combobox()) {
      this.combobox()!.comboboxList.nativeElement.scrollTo(0, 0);
    }
    this.checkSelectedStatus(this.options());
    this.filteredOptions.set(this.options());
  }

  updateModel(): void {
    this.updateModelHandler();
    if (this.isValueEmpty) {
      this.clearValue(this.options());
      timer(0).subscribe(() => {
        this.combobox()!.listboxEl.style.top = `${this.combobox()!.el.nativeElement.offsetHeight + 8}px`;
      });
    }
    this.combobox()!.searchText = '';
    this.combobox()!.inputViewChild.nativeElement.value = '';
    this.combobox()?.commonNullFilter({});
  }

  private resolver(options: ComboboxItemFilter[]): void {
    this.options.set(options);
    this.filteredOptions.set(options);
  }
}
